import React, { useState, useEffect } from 'react'
import { Box, Typography, Button, Snackbar, Switch, FormControlLabel, Chip } from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSave, faPhone, faPhoneSlash } from '@fortawesome/free-solid-svg-icons'
import { LoadingSpinner } from '@components/atoms'
import CompactInteractiveDaySelector from '@views/settings/callAvailability/components/CompactInteractiveDaySelector'
import WorkHoursTimePicker from '@views/settings/callAvailability/components/WorkHoursTimePicker'
import { useFetch } from '@hooks'
import { authenticatedFetch } from '@services/api/apiClient'
import { apiEndpoints, dateFormats } from '../../../consts'
import styles from './CallAvailabilityView.module.scss'

const DEFAULT_OPERATING_HOURS = {
  workdays: { pattern: 'mon-fri' },
  work_hours: { start: '09:00', end: '17:00', timezone: dateFormats.timezone },
  special_times: []
}

const CallAvailabilityView = ({ userID, userOrganization }) => {
  const [operatingHours, setOperatingHours] = useState(DEFAULT_OPERATING_HOURS)
  const [isAvailable, setIsAvailable] = useState(true)
  const [isWithinHours, setIsWithinHours] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [showSuccess, setShowSuccess] = useState(false)
  const [error, setError] = useState(null)

  const { data: hoursData, loading: loadingHours } = useFetch(
    apiEndpoints.userAvailabilityOperatingHours(userID),
    {
      skip: !userID,
      errorMessage: 'Failed to load operating hours',
      dependencies: [userID]
    }
  )

  const {
    data: availData,
    loading: loadingAvail,
    refetch: refetchAvailability
  } = useFetch(apiEndpoints.userAvailabilityUserById(userID), {
    skip: !userID,
    dependencies: [userID],
    errorMessage: 'Failed to load availability status'
  })

  useEffect(() => {
    if (hoursData?.operating_hours) {
      setOperatingHours(hoursData.operating_hours)
    }
  }, [hoursData])

  useEffect(() => {
    if (availData) {
      setIsAvailable(availData.isAvailable)
      setIsWithinHours(availData.availability_reason === 'operating_hours')
    }
  }, [availData])

  const isLoading = loadingHours || loadingAvail

  const handleAvailabilityToggle = async (event) => {
    const newAvailability = event.target.checked
    setIsAvailable(newAvailability)

    try {
      const statusType = newAvailability ? 'available' : 'busy_next_workday'
      const response = await authenticatedFetch(apiEndpoints.userAvailabilityUser(userID), {
        method: 'PUT',
        body: JSON.stringify({
          userID,
          organization: userOrganization,
          statusType: statusType
        })
      })

      if (!response.ok) {
        throw new Error('Failed to update availability')
      }

      setShowSuccess(true)

      await authenticatedFetch(apiEndpoints.googleSheetsSyncAllUsers, {
        method: 'POST'
      })
    } catch (err) {
      console.error('Error updating availability:', err)
      setError('Failed to update availability')
      setIsAvailable(!newAvailability)
    }
  }

  const handleWorkdaysChange = (workdaysConfig) => {
    setOperatingHours((prev) => ({
      ...prev,
      workdays: workdaysConfig
    }))
  }

  const handleWorkHoursChange = (workHoursConfig) => {
    setOperatingHours((prev) => ({
      ...prev,
      work_hours: workHoursConfig
    }))
  }

  const handle247Toggle = (event) => {
    setOperatingHours((prev) => ({
      ...prev,
      is_24_7: event.target.checked
    }))
  }

  const handleSave = async () => {
    setIsSaving(true)
    setError(null)

    try {
      const response = await authenticatedFetch(
        apiEndpoints.userAvailabilityOperatingHours(userID),
        {
          method: 'PUT',
          body: JSON.stringify({
            userID,
            organization: userOrganization,
            operating_hours: operatingHours
          })
        }
      )

      if (response.ok) {
        setShowSuccess(true)
        // Re-fetch availability so the Current Status indicator reflects the
        // hours that were just saved instead of the pre-save value
        refetchAvailability()
      } else {
        const errorData = await response.json()
        throw new Error(errorData.message || 'Failed to save operating hours')
      }
    } catch (err) {
      console.error('Error saving operating hours:', err)
      setError(err.message || 'Failed to save operating hours')
    } finally {
      setIsSaving(false)
    }
  }

  if (isLoading) {
    return <LoadingSpinner message='Loading operating hours...' />
  }

  return (
    <div className={styles.root}>
      {/* Page Header */}
      <Box className={styles.pageHeader}>
        <Typography variant='h4' component='h1' className={styles.pageTitle}>
          Call Availability
        </Typography>
        <Typography className={styles.pageSubtitle}>
          Configure your work schedule and availability for call transfers
        </Typography>
      </Box>

      {/* Availability Toggle Section */}
      <Box className={styles.availabilitySection}>
        <Box className={styles.availabilityHeader}>
          <Box className={styles.availabilityStatus}>
            <FontAwesomeIcon
              icon={isAvailable ? faPhone : faPhoneSlash}
              className={isAvailable ? styles.availableIcon : styles.unavailableIcon}
            />
            <Typography variant='h6'>
              Current Status:{' '}
              <Chip
                label={isAvailable ? 'Available' : 'Unavailable'}
                color={isAvailable ? 'primary' : 'default'}
                size='small'
              />
            </Typography>
          </Box>
          <FormControlLabel
            control={
              <Switch
                checked={isAvailable}
                onChange={handleAvailabilityToggle}
                color='primary'
                inputProps={{ 'aria-label': 'toggle-availability' }}
              />
            }
            label={isAvailable ? 'Accepting Calls' : 'Not Accepting Calls'}
          />
        </Box>
        {!isWithinHours && (
          <Typography variant='body2' color='textSecondary' className={styles.hoursNote}>
            Note: You are currently outside your operating hours
          </Typography>
        )}
      </Box>

      {/* Settings Content */}
      <Box className={styles.settingsWrapper}>
        <Box className={styles.settingsContent}>
          <Box className={styles.section}>
            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(operatingHours.is_24_7)}
                  onChange={handle247Toggle}
                  color='primary'
                  inputProps={{ 'aria-label': 'toggle-24-7' }}
                />
              }
              label='Available 24/7 (always open)'
            />
          </Box>

          {operatingHours.is_24_7 ? (
            <Box className={styles.section}>
              <Typography variant='h6' className={styles.sectionTitle}>
                Schedule
              </Typography>
              <Typography variant='body2' className={styles.sectionDescription}>
                This client is available 24/7 — calls are accepted every day, around the clock.
              </Typography>
              <Chip label='24/7' color='primary' />
            </Box>
          ) : (
            <>
              <Box className={styles.section}>
                <CompactInteractiveDaySelector
                  selectedDays={operatingHours.workdays?.custom_days || []}
                  onChange={(selectedDays) =>
                    handleWorkdaysChange({
                      pattern: 'custom',
                      custom_days: selectedDays
                    })
                  }
                />
              </Box>

              <Box className={styles.section}>
                <Typography variant='h6' className={styles.sectionTitle}>
                  Work Hours
                </Typography>
                <Typography variant='body2' className={styles.sectionDescription}>
                  Set your daily work start and end times with timezone
                </Typography>
                <WorkHoursTimePicker
                  workHours={operatingHours.work_hours || DEFAULT_OPERATING_HOURS.work_hours}
                  onChange={handleWorkHoursChange}
                />
              </Box>
            </>
          )}

          {/* Save Button at Bottom */}
          <Box className={styles.saveButtonContainer}>
            <Button
              variant='contained'
              color='primary'
              size='large'
              startIcon={<FontAwesomeIcon icon={faSave} />}
              onClick={handleSave}
              disabled={isSaving}
              className={styles.saveButton}
            >
              {isSaving ? 'Saving Changes...' : 'Save Operating Hours'}
            </Button>
          </Box>
        </Box>
      </Box>

      {error && (
        <Typography color='error' className={styles.errorMessage}>
          {error}
        </Typography>
      )}

      <Snackbar
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center'
        }}
        open={showSuccess}
        autoHideDuration={3000}
        onClose={() => setShowSuccess(false)}
        message='Operating hours saved successfully!'
      />
    </div>
  )
}

export default CallAvailabilityView
